import React from 'react';
import { useQuery } from '@apollo/client';
import { BookmarkPlus, Plus, Smile, UserRound, Wrench } from 'lucide-react';
import { ViewerDocument } from '@sandtech-hub-monorepo/ehub-client';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  AboutSection,
  LanguagesSection,
  Loading,
  PortfolioAndProjectsSection,
  SkillsSection,
  SocialProfileSection,
} from '@sandtech-hub-monorepo/ui-kit-site';

import { ProfileTabSections } from '../../routes/types';
import { AchievementCard } from '../../components/achievements/AchievementCard';
import { AchievementsEmptyState } from '../../components/achievements/AchievementsEmptyState';
import { useAchievements } from '../../data/useAchievements';
import { ProfileHeader } from './components/ProfileHeader';
import { PersonalInfoSidebar } from './components/PersonalInfoSidebar';
import { PriorCertificationsSection } from './components/PriorCertificationsSection';

/**
 * Shared empty-state look for this page (Figma 10708-66006): a small icon in
 * a bordered, transparent-background circle; a heading sized close to the
 * description below it; a "+" on the call-to-action button, which itself has
 * a transparent background. Each section supplies its own copy and icon.
 */
const emptyStateStyling = {
  headerClassName: 'text-heading',
  headerSizeClassName: 'text-sm mt-2 mb-1',
  iconWrapperClassName:
    'w-12 h-12 flex items-center justify-center rounded-full border-[1.5px] border-border',
  ctaIcon: <Plus className="h-4 w-4" />,
  // Button's hover isn't real CSS :hover - it's JS-driven (see Button.tsx),
  // swapping in the outline hoverVariant's `bg-primary text-primary-foreground`
  // on mouseenter. `bg-transparent` alone wins the background swap (via
  // tailwind-merge) but leaves the light hover text colour behind, so text
  // and icon vanish against the still-transparent background. Reasserting
  // both on real CSS hover keeps the button legible and gives it the same
  // light-tint hover every other outline/primary button in set-edu uses.
  buttonClassName: 'bg-transparent hover:bg-primary/10 hover:text-primary',
};

export const Profile = () => {
  const { achievements, loading: achievementsLoading } = useAchievements();
  const { data, loading } = useQuery(ViewerDocument, {
    fetchPolicy: 'cache-first',
  });

  if (loading) {
    return <Loading />;
  }

  const viewer = data?.viewer;

  const tabItemClassName =
    'shrink-0 whitespace-nowrap rounded-none relative border-b-2 border-b-transparent bg-transparent px-4 py-3 text-sm leading-5 font-normal text-heading shadow-none transition-none data-[state=active]:text-primary data-[state=active]:border-b-primary data-[state=active]:font-medium data-[state=active]:bg-card data-[state=active]:shadow-none';

  return (
    <div className="w-full px-4 sm:px-8 md:px-12 py-8 md:py-10 bg-background space-y-8 max-w-screen-2xl mx-auto">
      <ProfileHeader
        firstName={viewer?.firstName}
        middleName={viewer?.middleName}
        lastName={viewer?.lastName}
        title={viewer?.title}
        gender={viewer?.gender}
        profilePhoto={viewer?.profilePhoto}
        countryOfResidence={viewer?.countryOfResidence}
        countyOfResidence={viewer?.countyOfResidence}
        sandId={viewer?.sandId}
      />

      <Tabs defaultValue={ProfileTabSections.Personal} className="w-full">
        <TabsList className="flex justify-start overflow-x-auto rounded-none bg-transparent p-0 h-auto border-b border-border">
          <TabsTrigger
            value={ProfileTabSections.Personal}
            className={tabItemClassName}
          >
            Personal Information
          </TabsTrigger>
          <TabsTrigger
            value={ProfileTabSections.Achievements}
            className={tabItemClassName}
          >
            My Achievements
          </TabsTrigger>
        </TabsList>

        <TabsContent value={ProfileTabSections.Personal}>
          <div className="flex gap-6 items-start flex-col lg:flex-row mt-6">
            {/* Main column */}
            <div className="flex-1 min-w-0 w-full flex flex-col gap-6">
              <AboutSection
                title="About Me"
                about={viewer?.bio || ''}
                truncate
                editable
                emptyState={{
                  icon: <UserRound className="h-4 w-4 text-primary" />,
                }}
                emptyStateStyling={emptyStateStyling}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                <SkillsSection
                  editable
                  skills={viewer?.skills || []}
                  badgeClassName="border-0 bg-background text-foreground font-normal"
                  emptyState={{
                    header: 'Showcase your technical skills!',
                    main: 'Add your programming languages, frameworks, and tools to stand out to recruiters',
                    callToActionText: 'Add Skills',
                    icon: <Wrench className="h-4 w-4 text-primary" />,
                  }}
                  emptyStateStyling={emptyStateStyling}
                />
                <LanguagesSection
                  editable
                  languages={viewer?.languages || []}
                  emptyState={{
                    header: 'Add languages you speak!',
                    main: "List the languages you're fluent in to connect with global opportunities",
                    callToActionText: 'Add Language',
                    icon: <Smile className="h-4 w-4 text-primary" />,
                  }}
                  emptyStateStyling={emptyStateStyling}
                />
                <PortfolioAndProjectsSection
                  title="Portfolio & Projects"
                  urls={viewer?.portfolioAndProjects || []}
                  emptyState={{
                    header: 'Share your best work!',
                    main: 'Upload projects, case studies, or research papers to build your portfolio',
                    callToActionText: 'Add Project',
                    icon: <BookmarkPlus className="h-4 w-4 text-primary" />,
                  }}
                  emptyStateStyling={emptyStateStyling}
                />
              </div>

              <PriorCertificationsSection
                certifications={viewer?.priorCertifications}
              />
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-[400px] shrink-0 flex flex-col gap-6">
              <PersonalInfoSidebar
                sandId={viewer?.sandId}
                email={viewer?.primaryEmail}
                birthDate={viewer?.birthDate}
                gender={viewer?.gender}
                phoneNumber={viewer?.phoneNumber}
                countryOfOrigin={viewer?.countryOfOrigin}
                countryOfResidence={viewer?.countryOfResidence}
                countyOfResidence={viewer?.countyOfResidence}
              />
              <SocialProfileSection
                editable
                variant="monochrome"
                socialLinks={viewer?.socialLinks}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value={ProfileTabSections.Achievements}>
          {achievements.length ? (
            <div className="mt-6 flex flex-col gap-4">
              {achievements.map((enrollment) => (
                <AchievementCard key={enrollment.id} enrollment={enrollment} />
              ))}
            </div>
          ) : (
            !achievementsLoading && (
              <div className="mt-6">
                <AchievementsEmptyState />
              </div>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
